import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardMedia,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Visibility as VisibilityIcon, ShoppingCart as CartIcon } from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase-config'; // Firebase setup

const KnowledgeStore = () => {
  const [knowledgePacks, setKnowledgePacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch knowledge packs from Firestore
  const fetchKnowledgePacks = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'globalKnowledgeStore'));
      const packs = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setKnowledgePacks(packs);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching knowledge packs:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgePacks();
  }, []);

  // Handle opening/closing modal
  const handleOpenModal = (pack) => {
    setSelectedPack(pack);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPack(null);
  };

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h4" gutterBottom>
        Knowledge Store
      </Typography>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
          <CircularProgress />
        </div>
      ) : (
        <Grid container spacing={3}>
          {knowledgePacks.map((pack) => (
            <Grid item xs={12} sm={6} md={4} key={pack.id}>
              <Card style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={pack.thumbnailURL || '/default-pack-image.jpg'} // Use the `thumbnailURL` field or fallback to a placeholder
                  alt={pack.title}
                  style={{ objectFit: 'cover' }}
                />
                <CardContent style={{ flexGrow: 1 }}>
                  <Typography variant="h6">{pack.title}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {pack.description?.length > 100 ? pack.description.substring(0, 100) + '...' : pack.description}
                  </Typography>
                  <Typography variant="subtitle2" style={{ marginTop: '10px' }}>
                    Shared by: {pack.sharedBy || 'Anonymous'}
                  </Typography>
                </CardContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleOpenModal(pack)}
                  >
                    Preview
                  </Button>
                  <IconButton>
                    <CartIcon />
                  </IconButton>
                </div>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modal for previewing knowledge pack */}
      {selectedPack && (
        <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogContent>
            <Typography variant="h5" gutterBottom>
              {selectedPack.title}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {selectedPack.description}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Shared by: {selectedPack.sharedBy || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Files:
            </Typography>
            <List>
              {selectedPack.files && selectedPack.files.length > 0 ? (
                selectedPack.files.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={file.name} />
                    <IconButton onClick={() => window.open(file.url, '_blank')}>
                      <VisibilityIcon />
                    </IconButton>
                  </ListItem>
                ))
              ) : (
                <Typography>No files in this knowledge pack</Typography>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="primary">
              Close
            </Button>
            <Button color="primary" variant="contained" startIcon={<AddIcon />}>
              Add to Library
            </Button>
            <Button color="secondary" variant="contained" startIcon={<CartIcon />}>
              Purchase
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default KnowledgeStore;
